/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { KnotBlockerModel } from 'game/boardMVC/models/blockers/KnotBlockerModel';
import { RocketBlockerModel } from 'game/boardMVC/models/blockers/RocketBlockerModel';
import { isConnectedBlocker } from 'game/boardMVC/utils/isConnectedBlocker';
import * as HTMLElementCreator from '../../../libs/HTML/HTMLElementCreator';
import type { EditorPanel, EditorPanelStateChangeCallback } from './types';
import { createEditorInput, type EditorInputField } from '../utils/createEditorInput';
import type { BlockerModel } from '../../../game/boardMVC/types';
import { ScoreBlockerModel } from '../../../game/boardMVC/models/blockers/ScoreBlockerModel';
import { PlankBlockerModel } from '../../../game/boardMVC/models/blockers/PlankBlockerModel';
import { TapeBlockerModel } from '../../../game/boardMVC/models/blockers/TapeBlockerModel';
import { SECTION_LABEL_STYLE } from '../EditorOverlayStyles';
import { createEditorIncrementInput } from '../utils/createEditorIncrementInput';
import MathUtils from '../../../utils/MathUtils';
import type { TileStackModel } from '../../../game/boardMVC/models/board/TileStackModel';
import { CoinBlockerModel } from '../../../game/boardMVC/models/blockers/CoinBlockerModel';
import type { TileColor } from '../../../game/boardMVC/TileColor';

interface NumericalFieldConfig {
  label: string;
  restrictRegEx: RegExp;
  increment: number;
  getter(model: BlockerModel): string | null;
  setter(model: BlockerModel, value: number): void;
}

type FieldConfig = NumericalFieldConfig;

interface CustomFieldConfig {
  element: HTMLElement;
  isVisible(model: BlockerModel | null): boolean;
  onBlockerChanged(model: BlockerModel | null): void;
}

export function isHpBlocker(
  model: BlockerModel,
): model is BlockerModel & { maxHp: number; setHp(amount: number): void; getHp(): number } {
  return [PlankBlockerModel, TapeBlockerModel, KnotBlockerModel, RocketBlockerModel].some((v) => model instanceof v);
}

const fields: FieldConfig[] = [
  {
    label: 'Score ',
    restrictRegEx: /^\d+$/,
    increment: 25,
    getter: (model: BlockerModel): string | null => {
      if (!(model instanceof ScoreBlockerModel)) return null;
      return model.getScore().toString();
    },
    setter: (model: BlockerModel, value: number): void => {
      if (!(model instanceof ScoreBlockerModel)) return;
      model.setScore(value);
    },
  },
  {
    label: 'HP ',
    restrictRegEx: /^\d+$/,
    increment: 1,
    getter: (model: BlockerModel): string | null => {
      if (isHpBlocker(model)) {
        return model.getHp().toString();
      }
      return null;
    },
    setter: (model: BlockerModel, value: number): void => {
      if (!isHpBlocker(model)) return;
      const hp = MathUtils.clamp(value, 1, model.maxHp);
      model.setHp(hp);
    },
  },
  {
    label: 'Price ',
    restrictRegEx: /^\d+$/,
    increment: 25,
    getter: (model: BlockerModel): string | null =>
      model instanceof CoinBlockerModel ? model.getPrice().toString() : null,
    setter: (model: BlockerModel, value: number): void => {
      if (model instanceof CoinBlockerModel) {
        model.setPrice(value);
      }
    },
  },
  {
    label: 'Color ',
    restrictRegEx: /^\d+$/,
    increment: 1,
    getter(model: BlockerModel): string | null {
      if (!(model instanceof PlankBlockerModel)) return null;
      return `${model.getColor()}`;
    },
    setter(model: BlockerModel, value: TileColor): void {
      if (!(model instanceof PlankBlockerModel)) return;
      model.setColor(value);
    },
  },
];

export class EditorBlockersPanel implements EditorPanel {
  public readonly element: HTMLElement;

  private readonly editPanel: HTMLElement;

  private editInputs: (FieldConfig & { input: EditorInputField })[] = [];

  private readonly editorCustomInputs: CustomFieldConfig[] = [];

  private readonly editButtons: Record<string, HTMLButtonElement> = {};

  public stackModel: TileStackModel | null = null;

  constructor(
    private readonly onStateUpdate: EditorPanelStateChangeCallback,
    private readonly setStackHighlight: ((stackId: string | null) => void) | undefined,
    private readonly setBlockerConnectionsEditMode: () => void,
    private readonly refreshBlockerView: (stackModel: TileStackModel) => void,
  ) {
    this.element = HTMLElementCreator.createDiv({
      elementID: 'editor-header-properties-panel',
      parentElement: undefined,
      style: {
        position: 'fixed',
        top: '50px',
        right: '5px',
        padding: '2px 2px 2px 2px',
        color: 'black',
        minWidth: '200px',
        maxWidth: '200px',
        minHeight: '500px',
        backgroundColor: 'rgba(0,0,0,0.25)',
        pointerEvents: 'auto',
      },
    });

    HTMLElementCreator.createLabel({
      parentElement: this.element,
      labelText: 'Blockers',
      style: { ...SECTION_LABEL_STYLE },
    });

    this.editPanel = this.createEditPanel();
  }

  public destroy(): void {
    this.element.remove();
  }

  public focus(): void {
    this.element.style.visibility = 'visible';
  }

  public blur(): void {
    this.element.style.visibility = 'hidden';
    this.stopEditing();
  }

  private createEditPanel(): HTMLElement {
    const panel = HTMLElementCreator.createDiv({
      parentElement: this.element,
    });

    this.editInputs = fields.map((fieldConfig) => ({
      ...fieldConfig,
      input: this.createNumericalInput(fieldConfig),
    }));

    this.editorCustomInputs.push(this.createConnectionsField());

    // create button to edit connections
    this.editButtons.editConnections = HTMLElementCreator.createButton({
      parentElement: this.element,
      labelText: 'Edit Connections',
      onClick: this.setBlockerConnectionsEditMode,
      style: { width: '100%', marginTop: '2px' },
    });

    return panel;
  }

  private createNumericalInput(fieldConfig: NumericalFieldConfig): EditorInputField {
    const input = createEditorIncrementInput({
      parentElement: this.element,
      labelText: fieldConfig.label,
      increment: fieldConfig.increment,
      restrictRegEx: fieldConfig.restrictRegEx,
      inputStyle: {
        width: '40px',
      },
      onValueChanged: (value: string) => {
        const { stackModel } = this;
        if (!stackModel) return;

        const numValue = parseInt(value, 10);
        if (Number.isNaN(numValue)) return;
        if (!stackModel.blocker) return;

        fieldConfig.setter(stackModel.blocker, numValue);
        input.input.value = fieldConfig.getter(stackModel.blocker)?.toString() ?? '';
        this.refreshBlockerView(stackModel);
        this.onStateUpdate({});
      },
    });
    return input;
  }

  private createConnectionsField(): CustomFieldConfig {
    const connections = createEditorInput({
      parentElement: this.element,
      labelText: 'Connections ',
      onValueChanged: () => {},
    });
    connections.input.disabled = true;

    return {
      element: connections.panel,
      isVisible(model: BlockerModel): boolean {
        return isConnectedBlocker(model);
      },
      onBlockerChanged(model: BlockerModel): void {
        if (isConnectedBlocker(model)) {
          connections.input.value = model.connection
            .getAll()
            .map((cell) => `(${cell.x},${cell.y})`)
            .join(', ');
        }
      },
    };
  }

  public edit(stackModel: TileStackModel): void {
    this.setStackHighlight?.(stackModel.id);
    const blockerType = stackModel?.blocker?.getType();
    if (!blockerType) return;

    this.stackModel = stackModel;

    this.editInputs.forEach((editInput) => {
      if (!stackModel.blocker || editInput.getter(stackModel.blocker) === null) {
        this.toggleInputVisiblity(editInput.input.panel, false);
        return;
      }

      editInput.input.input.value = editInput.getter(stackModel.blocker) ?? '';
      this.toggleInputVisiblity(editInput.input.panel, true);
    });

    this.editorCustomInputs.forEach((editInput) => {
      editInput.onBlockerChanged(stackModel.blocker);
      this.toggleInputVisiblity(editInput.element, editInput.isVisible(stackModel.blocker));
    });

    if (stackModel.blocker) {
      this.toggleInputVisiblity(this.editButtons.editConnections, isConnectedBlocker(stackModel.blocker));
    }
  }

  private toggleInputVisiblity(element: HTMLElement, visible: boolean): void {
    element.style.display = visible ? 'block' : 'none';
  }

  public stopEditing(): void {
    this.setStackHighlight?.(null);
    this.stackModel = null;
    this.editPanel.remove();
  }
}
